import { memo, useMemo } from 'react';

interface MiniNode {
  cx: number;
  cy: number;
  r: number;
  color: string;
  parentIdx: number;
}

const PALETTES_LIGHT: ReadonlyArray<ReadonlyArray<string>> = [
  ['#8C5CF5', '#D946EF', '#06B6D4'],
  ['#F59E0B', '#EF4444', '#8C5CF5'],
  ['#10B981', '#06B6D4', '#3B82F6'],
  ['#EC4899', '#8C5CF5', '#FBBF24'],
  ['#3B82F6', '#10B981', '#F59E0B'],
];

const PALETTES_DARK: ReadonlyArray<ReadonlyArray<string>> = [
  ['#A78BFA', '#F0ABFC', '#67E8F9'],
  ['#FCD34D', '#FCA5A5', '#A78BFA'],
  ['#6EE7B7', '#67E8F9', '#93C5FD'],
  ['#F9A8D4', '#A78BFA', '#FDE68A'],
  ['#93C5FD', '#6EE7B7', '#FCD34D'],
];

const DEFAULT_PALETTE: ReadonlyArray<string> = [
  '#8C5CF5',
  '#D946EF',
  '#06B6D4',
];

function hash(seed: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < seed.length; index++) {
    value ^= seed.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

function seededRandom(seed: number): () => number {
  let state = seed || 1;
  return () => {
    state = Math.imul(state ^ (state >>> 15), 0x2c1b3c6d);
    state = Math.imul(state ^ (state >>> 12), 0x297a2d39);
    state ^= state >>> 15;
    return (state >>> 0) / 0x1_0000_0000;
  };
}

function buildNodes(seed: string, size: number, isDark: boolean): MiniNode[] {
  const seedHash = hash(seed);
  const random = seededRandom(seedHash);
  const palettes = isDark ? PALETTES_DARK : PALETTES_LIGHT;
  const colors = palettes[seedHash % palettes.length] ?? DEFAULT_PALETTE;
  const rootColor = colors[0] ?? '#8C5CF5';
  const branchColorA = colors[1] ?? rootColor;
  const branchColorB = colors[2] ?? rootColor;
  const center = size / 2;
  const nodes: MiniNode[] = [
    {
      cx: center,
      cy: center,
      r: size * 0.11,
      color: rootColor,
      parentIdx: -1,
    },
  ];
  const branchCount = 5 + Math.floor(random() * 2);
  const ringRadius = size * 0.32;
  const startAngle = random() * Math.PI * 2;

  for (let index = 0; index < branchCount; index++) {
    const angle = startAngle + (index / branchCount) * Math.PI * 2;
    nodes.push({
      cx: center + Math.cos(angle) * ringRadius,
      cy: center + Math.sin(angle) * ringRadius,
      r: size * (0.055 + random() * 0.025),
      color: index % 2 === 0 ? branchColorA : branchColorB,
      parentIdx: 0,
    });
  }

  return nodes;
}

export const MindmapMini = memo(function MindmapMini({
  seed,
  size,
  isDark,
  className,
}: {
  seed: string;
  size: number;
  isDark: boolean;
  className?: string;
}) {
  const nodes = useMemo(
    () => buildNodes(seed, size, isDark),
    [isDark, seed, size]
  );
  const lineColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)';

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
    >
      {nodes.map((node, index) => {
        if (node.parentIdx < 0) return null;
        const parent = nodes[node.parentIdx];
        if (!parent) return null;
        return (
          <line
            key={`line-${index}`}
            x1={parent.cx}
            y1={parent.cy}
            x2={node.cx}
            y2={node.cy}
            stroke={lineColor}
            strokeWidth={1}
          />
        );
      })}
      {nodes.map((node, index) => (
        <circle
          key={`node-${index}`}
          cx={node.cx}
          cy={node.cy}
          r={node.r}
          fill={node.color}
        />
      ))}
    </svg>
  );
});
